import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Modal,
  Paper,
  TextField,
  Typography,
  useTheme,
  alpha,
  Divider,
} from '@mui/material';
import React, { useState } from 'react';
import { useUser } from '../../contexts/UserContext';
import { UserProfileError, AuthenticationError } from '../../contexts/UserContext';
import { useConfig } from '../../contexts/ConfigContext';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

interface LoginModalProps {
  open: boolean;
  onClose?: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ 
  open,
  onClose = () => {}
}) => {
  const { login } = useUser();
  const config = useConfig();
  const theme = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await login(username, password);
      onClose?.();
    } catch (err) {
      if (err instanceof UserProfileError) {
        setError('Credentials correct but unable to retrieve user profile. Contact support.');
      } else if (err instanceof AuthenticationError) {
        setError('Invalid username or password');
      } else {
        setError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
  };

  // Custom styles for input fields
  const inputStyles = {
    '& .MuiFilledInput-root': {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
      borderRadius: 0.5,
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.09),
      },
      '&.Mui-focused': {
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
        boxShadow: 'none',
      },
      '&:before, &:after': {
        display: 'none', // Remove the underline
      },
    },
    '& .MuiInputLabel-root': {
      display: 'none', // Completely hide the label
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
    },
    mb: 2,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="login-modal-title"
    >
      <Paper sx={modalStyle}>
        <Box 
          sx={{ 
            width: '100%', 
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            py: 3.5,
            px: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography 
            id="login-modal-title" 
            variant="h4" 
            component="h2" 
            gutterBottom
            align="center"
            fontWeight="500"
          >
            Sign in to CultureMap
          </Typography>
        </Box>

        <Box sx={{ py: 4, px: 4, width: '100%' }}>
          {config.loginMessage && (
            <>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ mb: 2, textAlign: 'center' }}
              >
                {config.loginMessage}
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </>
          )}
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2, 
                width: '100%',
                borderRadius: 0.5
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <TextField
              fullWidth
              variant="filled"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
              required
              autoFocus
              aria-label="Username"
              InputLabelProps={{
                shrink: false,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineOutlinedIcon />
                  </InputAdornment>
                ),
              }}
              sx={inputStyles}
            />
            
            <Divider sx={{ my: 1 }} />
            
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              variant="filled"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              aria-label="Password"
              InputLabelProps={{
                shrink: false,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputStyles}
            />
            
            <Box sx={{ mt: 2, mb: 3, display: 'flex', justifyContent: 'center' }}>
              <img 
                src="/images/RPG Painted Circle 600px.png" 
                alt="RPG Logo" 
                width={80}
                style={{ 
                  borderRadius: '50%',
                  boxShadow: `0 2px 4px ${alpha('#000', 0.2)}`
                }}
              />
            </Box>
            
            <Box sx={{ mt: 1 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || !username || !password}
                fullWidth
                size="large"
                sx={{ 
                  py: 1.2, 
                  textTransform: 'none',
                  fontSize: '1rem',
                  borderRadius: 0.5,
                  boxShadow: 1
                }}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Button
                variant="text"
                disabled={isLoading}
                onClick={onClose}
                fullWidth
                sx={{ 
                  textTransform: 'none',
                  color: theme.palette.text.secondary,
                  borderRadius: 0.5
                }}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Modal>
  );
}; 